import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { CREW_MEDIA_BUCKET, CREW_MEDIA_PREFIX } from '../crewMedia';
import { AssetVariant, createAssetCache, imageTransform } from '../mediaAssetCache';

const cache = createAssetCache();
supabase.auth.onAuthStateChange(() => cache.clear());
export const resizedVaultImages = process.env.REACT_APP_VAULT_IMAGE_TRANSFORMS === 'true';
export function useAssetUrl(source?: string, variant: AssetVariant = 'original', enabled = true) {
  const [state, setState] = useState<{source?: string; variant?: AssetVariant; url: string; error: boolean}>({url:'',error:false});
  const [attempt, retry] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const cacheAsset = useRef('');
  useEffect(() => {
    if (!source || !enabled) return;
    let active = true;
    let timer: number | undefined;
    setState({source, variant, url:'', error:false});
    const resolve = async () => {
      try {
        let bucket = ''; let path = '';
        if (source.startsWith('storage://')) {
          if (!source.startsWith(CREW_MEDIA_PREFIX)) throw new Error('Invalid bucket');
          bucket = CREW_MEDIA_BUCKET; path = source.slice(CREW_MEDIA_PREFIX.length);
        } else {
          const u = new URL(source, window.location.origin);
          const expected = new URL(process.env.REACT_APP_SUPABASE_URL || 'https://placeholder.supabase.co');
          const prefix = '/storage/v1/object/public/production-assets/';
          if (u.pathname.startsWith(prefix)) {
            if (u.origin !== expected.origin) throw new Error('Invalid host');
            if (process.env.REACT_APP_LEGACY_ASSETS_PUBLIC !== 'true') { bucket='production-assets'; path=decodeURIComponent(u.pathname.slice(prefix.length)); }
          }
          if (!bucket) {
            if (!['https:', 'http:'].includes(u.protocol)) throw new Error('Unsupported asset');
            if (active) setState({source,variant,url:source,error:false});
            return;
          }
        }
        const decodedPath = decodeURIComponent(path);
        if (!path || decodedPath.includes('..') || decodedPath.includes('?') || decodedPath.includes('#') || decodedPath.includes('\\')) throw new Error('Invalid asset');
        cacheAsset.current = `${bucket}/${path}`;
        const { data } = await supabase.auth.getSession();
        if (!data.session) throw new Error('Sign in required');
        const actualVariant = resizedVaultImages ? variant : 'original';
        const url = await cache.resolve(data.session.access_token, `${bucket}/${path}`, actualVariant, async () => {
          const transform = imageTransform(actualVariant);
          const result = await supabase.storage.from(bucket).createSignedUrl(path, 300, transform ? {transform} : undefined);
          if (result.error || !result.data?.signedUrl) throw new Error('Asset unavailable');
          return result.data.signedUrl;
        });
        if (active) {
          setState({source,variant,url,error:false});
          timer = window.setTimeout(() => setRefresh(n => n + 1), Math.max(1000, cache.remaining(data.session.access_token, `${bucket}/${path}`, actualVariant)));
        }
      } catch { if (active) setState({source,variant,url:'',error:true}); }
    };
    void resolve();

    return () => { active=false; if (timer !== undefined) window.clearTimeout(timer); };
  }, [source,variant,enabled,attempt,refresh]);
  useEffect(() => {
    const {data} = supabase.auth.onAuthStateChange(() => {setState({url:'',error:false}); setRefresh(n => n + 1);});
    return () => data.subscription.unsubscribe();
  }, []);
  const matching = enabled && state.source === source && state.variant === variant;
  return {url: matching ? state.url : '', error: matching && state.error, retry: () => {cache.discard(cacheAsset.current); retry(n=>n+1);} };
}
export function PrivateImage(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  const {url,error} = useAssetUrl(props.src);
  return url ? <img {...props} alt={props.alt || ''} src={url}/> : <span role="status">{error ? 'Media unavailable' : 'Loading image…'}</span>;
}
export function PrivateVideo(props: React.VideoHTMLAttributes<HTMLVideoElement>) {
  const {url,error} = useAssetUrl(props.src);
  return url ? <video {...props} src={url}/> : <span role="status">{error ? 'Media unavailable' : 'Loading video…'}</span>;
}
export function PrivateLink(props: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const {url} = useAssetUrl(props.href);
  return <a {...props} href={url || undefined} aria-disabled={!url} onClick={e=>{if(!url){e.preventDefault();return;}props.onClick?.(e);}}>{props.children}</a>;
}
